/**
 * Copy for linking a business login and the booking (customer) account with the same email, and for
 * a refused switch between them. UK English, no em or en dashes (see account-link-copy.test.ts).
 *
 * side is the account the person is signed in to now: "business" (the dashboard) or "customer"
 * (their booking account).
 */
export type LinkSide = "business" | "customer";

export const LINK_TITLE: Record<LinkSide, string> = {
  business: "Link your booking account",
  customer: "Link your business account",
};

export const LINK_INTRO: Record<LinkSide, string> = {
  business:
    "A Book4 booking account already uses your email. If it is yours, enter its password to link it to this business account. After that you can switch between them without signing in again.",
  customer:
    "This booking account is not linked to your business account yet. Enter your business account password to link them. After that you can switch between them without signing in again.",
};

export const LINK_PASSWORD_LABEL: Record<LinkSide, string> = {
  business: "Booking account password",
  customer: "Business account password",
};

export const LINK_PASSWORD_HINT: Record<LinkSide, string> = {
  business: "The password you use to sign in to your booking account, not the one for this dashboard.",
  customer: "The password you use to sign in to your business dashboard, not the one for this account.",
};

export const LINK_FORGOT: Record<LinkSide, { label: string; href: string }> = {
  business: { label: "Forgot your booking account password?", href: "/auth/customer/forgot-password" },
  customer: { label: "Forgot your business account password?", href: "/auth/forgot-password" },
};

export const LINK_SUBMIT = "Link accounts";
export const LINK_SUBMITTING = "Linking...";
export const LINK_CANCEL = "Cancel";
export const LINK_CLOSE = "Close";
export const LINK_PASSWORD_REQUIRED = "Enter the password.";

export const LINKED_CAN_SWITCH = "Accounts linked. You can now switch between them.";
export const LINKED_NEEDS_VERIFY = "Accounts linked. Verify both emails to switch between them.";

export const SWITCH_LABEL: Record<LinkSide, string> = {
  business: "Customer dashboard",
  customer: "Business dashboard",
};

export const VERIFY_TITLE = "Verify your email to switch";

/** Why the switch is not available yet when an email still needs verifying, and how to fix it. */
export function verifyHelp(side: LinkSide, ownEmailVerified: boolean, otherEmailVerified: boolean | null): string {
  if (!ownEmailVerified) {
    return side === "business"
      ? "To switch to your booking account, first verify the email on this business account. Open the link we emailed you, or send a new one below."
      : "To switch to your business account, first verify the email on this booking account. Open the link we emailed you, or send a new one below.";
  }
  if (otherEmailVerified === false) {
    return side === "business"
      ? "Your booking account email is not verified yet. Sign in to your booking account and open the link in its verification email. You can send a new one from that account. Then you can switch."
      : "Your business account email is not verified yet. Sign in to your business dashboard and verify it from the banner at the top. Then you can switch.";
  }
  return "Both emails need to be verified before you can switch. Please check your inbox for the verification link.";
}

export const RESEND_LABEL = "Resend verification email";
export const RESEND_SENDING = "Sending...";
export const RESEND_SENT = "Verification email sent. Please check your inbox.";
export const RESEND_ALREADY = "Your email is already verified.";
export const RESEND_FAILED = "We could not send the email. Please try again.";

/** For a 429: too many tries (per IP) or the account is locked after wrong passwords. */
export function waitHelp(retryAfterSeconds?: number): string {
  if (!retryAfterSeconds) return "Too many attempts. Please wait a few minutes and try again.";
  const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
  return `Too many attempts. Please wait ${minutes === 1 ? "1 minute" : `${minutes} minutes`} and try again.`;
}

/** Added to a refused switch message so it says how to fix it. */
export function refusalFix(side: LinkSide, code: string | undefined, retryAfterSeconds?: number): string | null {
  if (code === "LINK_NOT_CONFIRMED") {
    return side === "business"
      ? `In your business dashboard, open the account menu, choose "${LINK_TITLE.business}" and enter your booking account password.`
      : `Sign in to your business dashboard with your password, open the account menu and choose "${LINK_TITLE.business}".`;
  }
  if (code === "EMAIL_NOT_VERIFIED") return "Verify your email using the link we sent, then try again.";
  if (code === "RATE_LIMITED" || code === "ACCOUNT_LOCKED") return waitHelp(retryAfterSeconds);
  return null;
}

/** The toast for a refused switch: the API's reason, then how to fix it. */
export function refusalToastText(
  side: LinkSide,
  refusedMessage: string | null,
  code?: string,
  retryAfterSeconds?: number
): string {
  if (code === "ACCOUNT_LOCKED") return refusedMessage ?? waitHelp(retryAfterSeconds);
  if (code === "RATE_LIMITED") return waitHelp(retryAfterSeconds);
  const fix = refusalFix(side, code, retryAfterSeconds);
  const message = refusedMessage ?? "";
  return fix ? `${message} ${fix}`.trim() : message;
}
